import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useUserRequests } from "../hooks/use-user-requests";
import {
  StatusFilterValue,
  useUserRequestFilters,
} from "../hooks/use-user-request-filters";
import { UserRequest, UserRequestStatus } from "../types/user-request";
import { ContentLayout } from "@/components/common/content-layout";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import { RequestDrawer } from "./request-drawer";
import UserRequestFilterBar from "./user-request-filter-bar";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const STALE_THRESHOLD_MS = 3 * 24 * 60 * 60 * 1000;
const PAGE_SIZE = 20;

function statusBadgeClass(status: UserRequestStatus): string {
  switch (status) {
    case "OPEN":
      return "bg-orange-100 text-orange-800 border-orange-200";
    case "REACHED_OUT":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "RESOLVED":
      return "bg-green-100 text-green-800 border-green-200";
    case "DECLINED":
      return "bg-red-100 text-red-800 border-red-200";
    case "SUPERSEDED":
    case "RETRACTED":
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
}

const STATUS_OPTIONS: Array<{ value: StatusFilterValue; label: string }> = [
  { value: "OPEN", label: "Open" },
  { value: "REACHED_OUT", label: "Reached Out" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "DECLINED", label: "Declined" },
  { value: "RETRACTED", label: "Retracted" },
  { value: "ALL", label: "All" },
];

interface Props {
  onUserClick: (userId: string) => void;
}

const ProfessionalChangeList = ({ onUserClick }: Props) => {
  const { page, search, status, sort, dateFrom, dateTo, filtersActive, setPage } =
    useUserRequestFilters();

  const { data, isLoading, isFetching, error } = useUserRequests({
    type: "PROFESSIONAL_CHANGE",
    status: status === "ALL" ? undefined : status,
    date_from: dateFrom,
    date_to: dateTo,
    q: search.trim() || undefined,
    sort,
    page,
    limit: PAGE_SIZE,
  });

  const [selectedRequest, setSelectedRequest] = useState<UserRequest | null>(
    null,
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const requests = data?.requests ?? [];
  const lastPage = data?.lastPage ?? 0;

  const openDrawerFor = (req: UserRequest) => {
    setSelectedRequest(req);
    setIsDrawerOpen(true);
  };

  return (
    <>
      <UserRequestFilterBar statusOptions={STATUS_OPTIONS} />

      <ContentLayout
        data={requests}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage="Failed to load professional change requests"
        containerClassName="h-full p-0 w-full"
        emptyState={{
          message: filtersActive
            ? "No requests match the current filters"
            : "No pending professional change requests",
        }}
      >
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-orange-500">
                <th className="text-left py-2 px-4 text-xs font-medium text-white">
                  User
                </th>
                <th className="text-left py-2 px-4 text-xs font-medium text-white">
                  Bundle
                </th>
                <th className="text-left py-2 px-4 text-xs font-medium text-white">
                  Reason
                </th>
                <th className="text-left py-2 px-4 text-xs font-medium text-white">
                  Submitted
                </th>
                <th className="text-left py-2 px-4 text-xs font-medium text-white">
                  Status
                </th>
                <th className="text-left py-2 px-4 text-xs font-medium text-white">
                  Resolution
                </th>
                <th className="text-right py-2 px-4 text-xs font-medium text-white w-[60px]" />
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => {
                const createdAt = new Date(req.created_at);
                const ageMs = Date.now() - createdAt.getTime();
                const isStale =
                  (req.status === "OPEN" || req.status === "REACHED_OUT") &&
                  ageMs > STALE_THRESHOLD_MS;
                const showResolution =
                  req.status === "RESOLVED" ||
                  req.status === "RETRACTED" ||
                  req.status === "DECLINED";

                return (
                  <tr
                    key={req.id}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => openDrawerFor(req)}
                  >
                    <td className="py-3 px-4">
                      <p className="text-sm font-medium text-gray-900">
                        {req.user.full_name}
                      </p>
                      {req.user.emails?.[0] && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {req.user.emails[0]}
                        </p>
                      )}
                      {req.user.phone_numbers?.[0] && (
                        <p className="text-xs text-gray-500">
                          {req.user.phone_numbers[0]}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700">
                      {req.bundle_usage?.bundle.name ?? (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700 max-w-[200px]">
                      {req.reason ? (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="block truncate cursor-default">
                                {req.reason}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[300px] whitespace-normal break-words">
                              {req.reason}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-500">
                      <div className="flex flex-col gap-1">
                        <span>{createdAt.toLocaleString()}</span>
                        {isStale && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 w-fit text-[10px]">
                            Stale
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge
                        className={`${statusBadgeClass(req.status)} text-[10px]`}
                      >
                        {req.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-600 max-w-[220px]">
                      {showResolution && req.resolved_at ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-gray-900">
                            {req.resolved_by_name ??
                              req.resolved_by ??
                              "unknown"}
                          </span>
                          <span className="text-gray-500">
                            {formatDistanceToNow(new Date(req.resolved_at), {
                              addSuffix: true,
                            })}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-400">›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {requests.length > 0 && (
          <div className="mt-4">
            <CustomPaginationComponent
              currentPage={page}
              lastPage={lastPage + 1}
              onPageChange={setPage}
            />
          </div>
        )}
      </ContentLayout>

      <RequestDrawer
        request={selectedRequest}
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        onViewUser={onUserClick}
        onLocalUpdate={setSelectedRequest}
      />
    </>
  );
};

export default ProfessionalChangeList;
